import { FC, useCallback, useEffect, useState } from "react";
import {
  Button,
  LoadingIndicator,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import { useMediaQuery } from "react-responsive";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "../molecules/Card";
import { CheckoutSnackbar } from "../atoms/CheckoutSnackbar";
import { HyperlinkButton } from "components/atoms/HyperlinkButton";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { usePersonaEmbeddedFlow } from "checkout/hooks/usePersonaEmbeddedFlow";
import {
  createPersonaInquiry,
  getPersonaInquiryStatus,
} from "services/personaInquiryService";
import { useAuthToken } from "hooks/useAuthToken";
import { useContactsData } from "utils/swrHooks";
import { getPrimaryContact } from "utils/contacts";
import { useProspect } from "checkout/hooks/useCheckoutSwr";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import { ProspectUnavailableModal } from "components/organisms/ProspectUnavailableModal";
import { ConfirmModal } from "checkout/components/molecules/ConfirmModal";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";

type ApprovalSnackbarState = {
  open: boolean;
  message: string;
  type: "success" | "error";
};

const TERMINAL_INQUIRY_STATUSES = new Set([
  "completed",
  "approved",
  "declined",
  "failed",
  "needs_review",
]);

const DISABLED_VERIFY_STATUSES = new Set([
  "failed",
  "approved",
  "needs_review",
]);

const isTerminalInquiryStatus = (status: string | null) =>
  !!status && TERMINAL_INQUIRY_STATUSES.has(status);

const ApprovalTaskView: FC = () => {
  const { prospectId } = useParams();
  const hybridToken = useAuthToken("HYBRID")?.token ?? "";
  const proposalToken = useAuthToken("PROPOSAL")?.token;
  const { proposalId } = useCheckoutContext();
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });
  const { openPersonaFlow, isOpening } = usePersonaEmbeddedFlow();
  const [verifyStatus, setVerifyStatus] = useState<string | null>(null);
  const [linkStatus, setLinkStatus] = useState<string | null>(null);
  const [isStatusLoading, setIsStatusLoading] = useState(true);
  const [isSendingLink, setIsSendingLink] = useState(false);
  const [isRetryModalOpen, setIsRetryModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<ApprovalSnackbarState>({
    open: false,
    message: "",
    type: "error",
  });

  const showSnackbar = (
    message: string,
    type: ApprovalSnackbarState["type"]
  ) => {
    setSnackbar({ open: true, message, type });
  };

  const { contacts, contactsError, isContactsLoading } = useContactsData(
    prospectId as string,
    hybridToken,
    proposalToken
  );
  const {
    data: prospect,
    isLoading: isProspectLoading,
    error: prospectError,
  } = useProspect(prospectId);

  const primaryContact = contacts ? getPrimaryContact(contacts) : undefined;
  const contactId = primaryContact?.contactId;

  const refreshInquiryStatus = useCallback(async () => {
    if (!prospectId || !proposalId || !contactId || !hybridToken) {
      return;
    }
    setIsStatusLoading(true);
    try {
      const result = await getPersonaInquiryStatus({
        prospectId,
        proposalId,
        contactId,
        hybridToken,
      });
      setVerifyStatus(result.verifyStatus);
      setLinkStatus(result.linkStatus);
    } catch (error) {
      console.error("Failed to load Persona inquiry status", error);
    } finally {
      setIsStatusLoading(false);
    }
  }, [contactId, hybridToken, proposalId, prospectId]);

  useEffect(() => {
    refreshInquiryStatus();
  }, [refreshInquiryStatus]);

  const formatStatus = (status: string | null) => {
    if (!status) {
      return "Not started";
    }
    const label = status.replace(/_/g, " ");
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  const handleSendLink = async () => {
    if (!prospectId || !contactId) {
      showSnackbar(
        "Unable to send the identity verification link. Contact info is missing.",
        "error"
      );
      return;
    }
    setIsSendingLink(true);
    try {
      await createPersonaInquiry({
        prospectId,
        proposalId,
        contactId,
        hybridToken: hybridToken || undefined,
        firstName: primaryContact?.customerFirstName,
        lastName: primaryContact?.customerLastName,
        street: prospect?.customerStreet,
        city: prospect?.customerCity,
        state: prospect?.customerState,
        postalCode: prospect?.customerZipCode,
        inquiryType: "link",
      });
      await refreshInquiryStatus();
      showSnackbar(
        "Identity verification link sent to the customer.",
        "success"
      );
    } catch (error) {
      console.error("Failed to send Persona link inquiry", error);
      showSnackbar(
        "Unable to send the identity verification link. Please try again.",
        "error"
      );
    } finally {
      setIsSendingLink(false);
    }
  };

  const handleVerify = () => {
    if (!prospectId || !contactId) {
      showSnackbar(
        "Unable to start identity verification. Contact info is missing.",
        "error"
      );
      return;
    }
    openPersonaFlow({
      prospectId,
      proposalId,
      contactId,
      onInquiryFinished: refreshInquiryStatus,
      onError: (message) => showSnackbar(message, "error"),
      hybridToken: hybridToken || undefined,
      firstName: primaryContact?.customerFirstName,
      lastName: primaryContact?.customerLastName,
      street: prospect?.customerStreet,
      city: prospect?.customerCity,
      state: prospect?.customerState,
      postalCode: prospect?.customerZipCode,
    });
  };

  if (prospectError) {
    return <ProspectUnavailableModal />;
  }

  if (contactsError) {
    return (
      <ErrorComponent
        context="ApprovalTaskView"
        action="Get Contacts"
        error={contactsError}
      />
    );
  }

  if (isContactsLoading || !contacts || isProspectLoading) {
    return <LoadingIndicator fullScreen />;
  }

  const showVerifySection =
    isStatusLoading ||
    isTerminalInquiryStatus(verifyStatus) ||
    !isTerminalInquiryStatus(linkStatus);
  const showLinkSection =
    isStatusLoading ||
    isTerminalInquiryStatus(linkStatus) ||
    !isTerminalInquiryStatus(verifyStatus);
  const personaReady = !isStatusLoading && !!contactId && !!hybridToken;
  const verifyDisabled =
    !!verifyStatus && DISABLED_VERIFY_STATUSES.has(verifyStatus);
  const isLinkFinished = isTerminalInquiryStatus(linkStatus);
  const isLinkRetry = linkStatus !== null && !isLinkFinished;

  return (
    <>
      <ContentWrapper isMobile={isMobile}>
        <Typography>
          Please complete our identification confirmation process.
        </Typography>
        <Typography>
          If you don&apos;t see the update you expected, please{" "}
          <HyperlinkButton
            onClick={() => {
              window.location.reload();
            }}
          >
            refresh this page
          </HyperlinkButton>
        </Typography>

        <Card>
          <CardContent>
            {showVerifySection && (
              <InquiryAction>
                <VerifyCardRow isMobile={isMobile}>
                  <Typography size={tokens.FONT_SIZE_3}>
                    Verify your identity before continuing
                  </Typography>
                  <VerifyActions>
                    <StyledButton
                      isMobile={isMobile}
                      disabled={!personaReady || isOpening || verifyDisabled}
                      onClick={handleVerify}
                      data-testid="checkout-approval-verify-button"
                    >
                      {isStatusLoading ? (
                        <LoadingIndicator
                          color="white"
                          showLoadingMessage={false}
                        />
                      ) : isOpening ? (
                        "Opening…"
                      ) : (
                        "Verify"
                      )}
                    </StyledButton>
                  </VerifyActions>
                </VerifyCardRow>
                <Typography data-testid="checkout-approval-verify-status">
                  Current status: {formatStatus(verifyStatus)}
                </Typography>
              </InquiryAction>
            )}
            {showVerifySection && showLinkSection && <SectionDivider />}
            {showLinkSection && (
              <InquiryAction>
                <VerifyCardRow isMobile={isMobile}>
                  <Typography size={tokens.FONT_SIZE_3}>
                    Send a verification link to the customer
                  </Typography>
                  {!isLinkFinished && (
                    <VerifyActions>
                      <StyledButton
                        isMobile={isMobile}
                        disabled={!personaReady || isSendingLink}
                        onClick={() =>
                          isLinkRetry
                            ? setIsRetryModalOpen(true)
                            : handleSendLink()
                        }
                        data-testid="checkout-approval-send-link-button"
                      >
                        {isStatusLoading ? (
                          <LoadingIndicator
                            color="white"
                            showLoadingMessage={false}
                          />
                        ) : isSendingLink ? (
                          "Sending…"
                        ) : isLinkRetry ? (
                          "Retry sending link"
                        ) : (
                          "Send Link To Customer"
                        )}
                      </StyledButton>
                    </VerifyActions>
                  )}
                </VerifyCardRow>
                <Typography data-testid="checkout-approval-link-status">
                  Current status: {formatStatus(linkStatus)}
                </Typography>
              </InquiryAction>
            )}
          </CardContent>
        </Card>
      </ContentWrapper>
      {isRetryModalOpen && (
        <ConfirmModal
          title="Are you sure?"
          confirmLabel="Resend"
          cancelLabel="Cancel"
          onClose={() => setIsRetryModalOpen(false)}
          onConfirm={() => {
            setIsRetryModalOpen(false);
            handleSendLink();
          }}
        >
          <Typography>
            Sending multiple messages can cause issues. Please confirm the
            customer did not receive the text in their main or spam folders.
          </Typography>
        </ConfirmModal>
      )}
      <CheckoutSnackbar
        open={snackbar.open}
        autoHideDuration={4000}
        colorScheme="light"
        message={snackbar.message}
        type={snackbar.type}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      />
    </>
  );
};

const ContentWrapper = styled.div<{ isMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InquiryAction = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const SectionDivider = styled.hr`
  background-color: ${tokens.OFFWHITE_40};
  border: none;
  height: 1px;
  margin: 0;
  width: 100%;
`;

const VerifyCardRow = styled.div<{ isMobile?: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  ${({ isMobile }) =>
    isMobile &&
    `
    flex-direction: column;
    align-items: stretch;
  `}
`;

const VerifyActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isMobile",
})<{ isMobile?: boolean }>`
  flex-shrink: 0;
  min-width: 120px;
  ${({ isMobile }) =>
    isMobile &&
    `
    width: 100%;
  `}
`;

export { ApprovalTaskView };
